<script setup lang="ts">
import {
  companyAddressLine,
  formatSiret,
  missingClientFields,
  type QuoteClientCheck,
  type QuoteClientMatch,
  type QuoteDraftClient,
} from '@cockpit/shared';
import { computed } from 'vue';
import { setClientField, sourceLabel } from '../../quoteDraft';

/**
 * Lot AT [8, 9] : « Le client dans Axonaut ». Trouvé (par le SIRET, puis par le nom) : rien à créer. Absent : il
 * sera créé à la validation, avec les champs remplis depuis le registre public, les infos du R1, l'invitation de
 * l'agenda et les notes (source en vert sous chaque champ tant qu'il n'est pas modifié). Un nom qui ressemble est
 * signalé : « Oui, prendre celui-là » ou « Non, créer un nouveau client ». Les infos manquantes sont « à compléter »,
 * jamais bloquantes.
 */
const props = defineProps<{
  client: QuoteDraftClient;
  check: QuoteClientCheck | null;
  searching: boolean;
  readonly?: boolean;
}>();
const emit = defineEmits<{
  update: [client: QuoteDraftClient];
  search: [];
  take: [match: QuoteClientMatch];
  dismiss: [match: QuoteClientMatch];
  notThisOne: [];
}>();

const missing = computed(() => new Set(missingClientFields(props.client).map((m) => m.field)));
const missingList = computed(() => missingClientFields(props.client));
/** Doublons possibles (noms qui ressemblent), pas encore écartés, quand le client est à créer. */
const duplicates = computed(() =>
  props.client.mode === 'new'
    ? (props.check?.matches ?? []).filter((m) => !props.client.dismissedMatches.includes(m.axonautCompanyId))
    : [],
);
const axonautUrl = computed(() =>
  props.client.axonautCompanyId
    ? `https://axonaut.com/business/company/show/${encodeURIComponent(props.client.axonautCompanyId)}`
    : null,
);
const foundBy = computed(() => {
  switch (props.client.foundBy) {
    case 'siret':
      return 'le SIRET';
    case 'name':
      return 'le nom';
    case 'r1':
      return 'le client du rendez-vous';
    case 'chosen':
      return 'ton choix';
    default:
      return null;
  }
});

function set(path: string, value: string | boolean): void {
  emit('update', setClientField(props.client, path, value));
}

const COMPANY_FIELDS = [
  { path: 'company.name', label: 'Raison sociale', full: false },
  { path: 'company.siret', label: 'SIRET du siège', full: false },
  { path: 'company.street', label: 'Adresse (rue)', full: true },
  { path: 'company.zipCode', label: 'Code postal', full: false },
  { path: 'company.city', label: 'Ville', full: false },
] as const;
const CONTACT_FIELDS = [
  { path: 'contact.firstname', label: 'Prénom', missingKey: 'contact.name' },
  { path: 'contact.lastname', label: 'Nom', missingKey: 'contact.name' },
  { path: 'contact.role', label: 'Fonction', missingKey: '' },
  { path: 'contact.email', label: 'E-mail', missingKey: 'contact.email' },
  { path: 'contact.phone', label: 'Téléphone', missingKey: 'contact.phone' },
] as const;

function value(path: string): string {
  const [group, key] = path.split('.') as ['company' | 'contact', string];
  const v = (props.client[group] as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : '';
}
function isMissing(path: string, missingKey?: string): boolean {
  if (path === 'company.street' || path === 'company.zipCode' || path === 'company.city')
    return missing.value.has('company.address') && value(path) === '';
  return missing.value.has(missingKey ?? path) && value(path) === '';
}
</script>

<template>
  <div class="q-client" data-testid="quote-client">
    <div v-if="client.mode === null" class="placeholder">
      <p>Le Cockpit cherche le client dans Axonaut par son SIRET, puis par son nom.</p>
      <button
        class="btn sm primary"
        type="button"
        :disabled="searching || readonly"
        data-testid="quote-client-search"
        @click="emit('search')"
      >
        {{ searching ? 'Recherche…' : 'Chercher le client dans Axonaut' }}
      </button>
    </div>

    <template v-else-if="client.mode === 'existing'">
      <div class="cli-banner ok" data-testid="quote-client-found">
        <span class="chip c-ok">Trouvé</span
        ><span
          ><b>Client trouvé dans Axonaut : {{ client.company.name }}</b
          ><template v-if="client.company.siret">
            (SIRET {{ formatSiret(client.company.siret) }})</template
          ></span
        >
      </div>
      <dl class="kvs">
        <template v-if="companyAddressLine(client.company)"
          ><dt>Adresse</dt>
          <dd>{{ companyAddressLine(client.company) }}</dd></template
        >
        <template v-if="foundBy"
          ><dt>Trouvé par</dt>
          <dd>{{ foundBy }}</dd></template
        >
      </dl>
      <div class="row">
        <a
          v-if="axonautUrl"
          class="btn sm"
          :href="axonautUrl"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="quote-client-axonaut"
          >Ouvrir la fiche dans Axonaut</a
        >
        <button
          v-if="!readonly"
          class="linkish"
          type="button"
          data-testid="quote-client-not-this-one"
          @click="emit('notThisOne')"
        >
          Ce n’est pas ce client
        </button>
      </div>
    </template>

    <template v-else>
      <div
        v-for="m in duplicates"
        :key="m.axonautCompanyId"
        class="cli-banner dup"
        data-testid="quote-client-duplicate"
      >
        <span
          ><b>Un client qui ressemble existe déjà dans Axonaut : « {{ m.name }} »</b> ({{
            [
              m.city,
              m.siret ? `SIRET ${formatSiret(m.siret)}` : 'sans SIRET',
              m.createdOn ? `créé le ${m.createdOn}` : null,
            ]
              .filter(Boolean)
              .join(', ')
          }}). C’est le même ?</span
        >
        <div class="row">
          <button
            class="btn sm primary"
            type="button"
            :disabled="readonly"
            data-testid="quote-client-take"
            @click="emit('take', m)"
          >
            Oui, prendre celui-là
          </button>
          <button
            class="btn sm"
            type="button"
            :disabled="readonly"
            data-testid="quote-client-dismiss"
            @click="emit('dismiss', m)"
          >
            Non, créer un nouveau client
          </button>
        </div>
      </div>
      <div class="cli-banner new" data-testid="quote-client-new">
        <span class="chip c-warn">À créer</span
        ><span><b>Client absent d’Axonaut : il sera créé</b> à la validation, avec ce qui suit.</span>
      </div>
      <p v-if="check?.registryError" class="muted q-small">{{ check.registryError }}</p>
    </template>

    <div v-if="client.mode !== null" class="form-grid">
      <template v-if="client.mode === 'new'">
        <label
          v-for="f in COMPANY_FIELDS"
          :key="f.path"
          class="field"
          :class="{ full: f.full, 'q-missing': isMissing(f.path) }"
          ><span
            >{{ f.label }}
            <span v-if="sourceLabel(client, f.path)" class="r1-from">{{ sourceLabel(client, f.path) }}</span>
            <span v-else-if="isMissing(f.path)" class="q-tocomplete">à compléter</span></span
          ><input
            type="text"
            :class="{ 'r1-prefilled': !!sourceLabel(client, f.path) }"
            :value="value(f.path)"
            :disabled="readonly"
            :data-testid="`quote-${f.path.replace('.', '-')}`"
            @change="set(f.path, ($event.target as HTMLInputElement).value)"
        /></label>
      </template>
      <span class="form-sub">Contact</span>
      <label
        v-for="f in CONTACT_FIELDS"
        :key="f.path"
        class="field"
        :class="{ 'q-missing': isMissing(f.path, f.missingKey) }"
        ><span
          >{{ f.label }}
          <span v-if="sourceLabel(client, f.path)" class="r1-from">{{ sourceLabel(client, f.path) }}</span>
          <span v-else-if="isMissing(f.path, f.missingKey)" class="q-tocomplete">à compléter</span></span
        ><input
          :type="f.path === 'contact.email' ? 'email' : 'text'"
          :class="{ 'r1-prefilled': !!sourceLabel(client, f.path) }"
          :value="value(f.path)"
          :disabled="readonly"
          :data-testid="`quote-${f.path.replace('.', '-')}`"
          @change="set(f.path, ($event.target as HTMLInputElement).value)"
      /></label>
      <label v-if="client.mode === 'new'" class="field full q-check"
        ><input
          type="checkbox"
          :checked="client.company.isProspect"
          :disabled="readonly"
          data-testid="quote-company-prospect"
          @change="set('company.isProspect', ($event.target as HTMLInputElement).checked)"
        />
        Créé comme prospect (pas encore client)</label
      >
    </div>
    <p
      v-if="client.mode !== null && missingList.length > 0"
      class="q-missing-note"
      data-testid="quote-client-missing"
    >
      À compléter si tu l’as : {{ missingList.map((m) => m.label.toLowerCase()).join(', ') }}. Rien n’est
      bloquant : le devis peut être créé sans.
    </p>
    <p v-if="client.mode === 'new'" class="lock-note">
      Axonaut n’a pas de champ SIREN : on y met le SIRET du siège, lu dans le registre public des entreprises
      (Recherche d’entreprises). Pas de copie de Pappers ni de societe.com.
    </p>
    <div v-if="client.mode !== null && !readonly" class="row">
      <button
        class="linkish"
        type="button"
        :disabled="searching"
        data-testid="quote-client-research"
        @click="emit('search')"
      >
        {{ searching ? 'Recherche…' : 'Chercher à nouveau dans Axonaut' }}
      </button>
    </div>
  </div>
</template>
